import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ChevronDown, ChevronUp, Trash2, CalendarX } from 'lucide-react';
import { useFetchAppointments } from '../../hooks/useFetchAppointments';
import { useDeleteDocument } from '../../hooks/useDeleteDocument';
import { useToast } from '../ui';
import { formatLocalDateInput } from '../../utilities/dateUtils';
import { Card, Badge, Skeleton, Button } from '../ui';

function AppointmentRow({ d, onCancel }) {
  const [expanded, setExpanded] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async (e) => {
    setCancelling(true);
    try { await onCancel(e, d); } finally { setCancelling(false); }
  };

  return (
    <div className="border-b border-border last:border-0">
      {/* Linha principal */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-secondary transition-colors duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-focus"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-text-primary">{d.hour?.[0]}</span>
            <Badge variant="primary">{d.service}</Badge>
            <Badge variant="neutral">{d.professional}</Badge>
          </div>
          <p className="text-xs text-text-muted mt-0.5 truncate">{d.name}</p>
        </div>
        {expanded ? <ChevronUp size={16} className="shrink-0 text-text-muted" /> : <ChevronDown size={16} className="shrink-0 text-text-muted" />}
      </button>

      {/* Detalhes expandidos */}
      {expanded && (
        <div className="px-4 pb-4 flex flex-col gap-2 bg-bg-secondary">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm pt-2">
            <div><dt className="text-xs text-text-muted">Cliente</dt><dd className="font-medium text-text-primary">{d.name}</dd></div>
            <div><dt className="text-xs text-text-muted">Celular</dt>
              <dd>
                <Link
                  to={`https://api.whatsapp.com/send?phone=${(d.cel || '').replace(/\(|\)|-| /g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                >
                  <Phone size={12} aria-hidden="true" /> {d.cel}
                </Link>
              </dd>
            </div>
            <div><dt className="text-xs text-text-muted">Serviço</dt><dd className="font-medium text-text-primary">{d.service}</dd></div>
            <div><dt className="text-xs text-text-muted">Horário</dt><dd className="font-medium text-text-primary">{d.hour?.[0]}</dd></div>
            <div className="col-span-2"><dt className="text-xs text-text-muted">ID</dt><dd className="font-mono text-xs text-text-muted break-all">{d.tid}</dd></div>
          </dl>
          <Button variant="danger" size="sm" loading={cancelling} onClick={handleCancel} className="self-start mt-1">
            <Trash2 size={14} /> Cancelar agendamento
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Agenda() {
  const todaysDate = formatLocalDateInput();
  const [date, setDate] = useState(todaysDate);
  const [professional, setProfessional] = useState('');
  const [docFiltered, setDocFiltered] = useState([]);

  const { documents, loading, error } = useFetchAppointments('transactions', date);
  const { deleteData } = useDeleteDocument();
  const toast = useToast();

  useEffect(() => { setDocFiltered(documents || []); }, [documents]);

  const handleProfessionalFilter = (e) => {
    const val = e.target.value;
    setProfessional(val);
    setDocFiltered(val ? (documents || []).filter((d) => d.professional === val) : (documents || []));
  };

  const handleCancelAppointment = async (e, appointment) => {
    e.preventDefault();
    const docId = appointment.id || appointment.tid;
    if (!docId) return;
    const deleted = await deleteData('transactions', docId);
    if (!deleted) {
      toast.error('Não foi possível cancelar o agendamento. Tente novamente.');
    } else {
      toast.success('Agendamento cancelado.');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Filtros */}
      <Card>
        <Card.Body className="py-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex flex-col gap-1 flex-1">
              <label htmlFor="filter-date" className="text-xs font-medium text-text-muted">Data</label>
              <input
                id="filter-date"
                type="date"
                value={date}
                onChange={(e) => { setDate(e.target.value); setProfessional(''); }}
                className="rounded-md border border-border px-3 py-2 text-sm bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus"
              />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <label htmlFor="filter-pro" className="text-xs font-medium text-text-muted">Barbeiro</label>
              <select
                id="filter-pro"
                value={professional}
                onChange={handleProfessionalFilter}
                className="rounded-md border border-border px-3 py-2 text-sm bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus appearance-none"
              >
                <option value="">Todos</option>
                <option value="Carlos">Carlos</option>
                <option value="Donizete">Donizete</option>
              </select>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Lista */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-text-primary">
              Agendamentos{' '}
              {!loading && (
                <span className="text-text-muted font-normal">({docFiltered.length})</span>
              )}
            </h2>
          </div>
        </Card.Header>

        {loading ? (
          <Card.Body className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </Card.Body>
        ) : error ? (
          <Card.Body>
            <p className="text-sm text-danger-text">Erro ao carregar agendamentos.</p>
          </Card.Body>
        ) : docFiltered.length === 0 ? (
          <Card.Body>
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CalendarX size={28} className="text-text-muted" aria-hidden="true" />
              <p className="text-sm text-text-muted">Nenhum agendamento para este filtro.</p>
            </div>
          </Card.Body>
        ) : (
          <div>
            {docFiltered.map((d) => (
              <AppointmentRow key={d.id} d={d} onCancel={handleCancelAppointment} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
